'use client'

import { Comment } from '../Comment'
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline'

export const CommentList = ({ comments, onReply }: CommentListProps) => {
  // Build a tree structure from flat comments
  const buildCommentTree = () => {
    const commentMap = new Map<number, CommentWithChildren>()
    const rootComments: CommentWithChildren[] = []

    // First pass: create a map of all comments
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, children: [] })
    })

    // Second pass: build the tree
    comments.forEach(comment => {
      const commentNode = commentMap.get(comment.id)!
      if (comment.responseTo && commentMap.has(comment.responseTo)) {
        // This is a reply, add it to its parent's children
        const parent = commentMap.get(comment.responseTo)!
        parent.children.push(commentNode)
      } else {
        // This is a root comment
        rootComments.push(commentNode)
      }
    })

    return rootComments
  }

  const renderComment = (
    comment: CommentWithChildren,
    depth: number = 0,
    isEven: boolean = false
  ): React.ReactNode => {
    return (
      <Comment
        key={comment.id}
        comment={comment}
        onReply={onReply}
        depth={depth}
        isEven={isEven}
      >
        {comment.children.length > 0 && (
          <div className="space-y-1">
            {comment.children.map(child =>
              renderComment(child, depth + 1, !isEven)
            )}
          </div>
        )}
      </Comment>
    )
  }

  const commentTree = buildCommentTree()

  if (commentTree.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 border border-gray-200">
        <div className="max-w-md mx-auto">
          <ChatBubbleLeftEllipsisIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No comments yet
          </h3>
          <p className="text-gray-600">
            Be the first to share your thoughts on this article!
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {commentTree.map((comment, index) =>
        renderComment(comment, 0, index % 2 === 0)
      )}
    </div>
  )
}

interface CommentListProps {
  comments: Array<{
    id: number
    customerId: number
    comment: string
    time: string
    responseTo?: number | null
    customerName: string
    gravatarUrl?: string | null
  }>
  onReply: (commentId: number, customerName: string) => void
}

type CommentWithChildren = CommentListProps['comments'][0] & {
  children: CommentWithChildren[]
}
